import React, { useContext, useEffect, useRef, useState } from 'react';
import Fuse from 'fuse.js';
import { orderBy } from 'lodash';
import {
  Grid,
  IconButton,
  Icons,
  Menu,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  Typography,
  TableSortLabel,
  Pagination,
  Tooltip,
} from '@nextiva/next-ui-components';
import clsx from 'clsx';
import { SkeletonAllRows } from 'components/Skeleton';
import { DataImportContext } from './context';
import FileModal from './FileModal';
import {
  DownloadIcon,
  CompletedWithErrorsIcon,
  FailedIcon,
  CompletedIcon,
  HistorySearch,
  HistoryTableRow,
  ImportButton,
  MenuIcon,
  ProgressBar,
  StyledTableCell,
  StyledProgressBarText,
  StyledDateTableCell,
  StyledStatusCell,
  StyledBox,
} from './HistoryLog.styles';
import { ImportStatus } from './ImportStatus';
import { useGetDataImportHistory } from './hooks/useDataImportHistory';
import { HistoryTableData } from './types';
import { formatJobHistory } from './utils';
import { DownloadErrorButton } from './DownloadErrorButton';

type Order = 'asc' | 'desc';

const sortFunction = (item, sortField): HistoryTableData => {
  if (sortField === 'numOfRecords' || sortField === 'fileSize') {
    return item[sortField] as HistoryTableData;
  }
  return item[sortField] && (item[sortField].toLowerCase() as HistoryTableData);
};

const sortData = (
  TableData: HistoryTableData[],
  sortOrder: Order,
  orderField: string,
) => {
  return orderBy(
    TableData,
    [(item): HistoryTableData => sortFunction(item, orderField)],
    [sortOrder],
  );
};

interface HistoryLogHeader {
  id: string;
  label: string;
}

const headCells: HistoryLogHeader[] = [
  { id: 'fileName', label: 'File name' },
  { id: 'createdBy', label: 'Created by' },
  { id: 'createdDate', label: 'Date' },
  { id: 'entityType', label: 'Import type' },
  { id: 'numOfRecords', label: '# of records' },
  { id: 'fileSize', label: 'File size' },
  { id: 'status', label: 'Processing status' },
];

const HistoryRow = ({ entry }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openModal, setOpenModal] = useState(false);

  const onClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const {
    id,
    fileName,
    createdDate,
    lastModifiedDate,
    createdBy,
    entityType,
    numOfRecords,
    fileDisplay,
    progress,
    status,
  }: HistoryTableData = entry;

  const isCompleted = status === ImportStatus.completed;
  const isCompletedWithErrors = status === ImportStatus.completedWithErrors;
  const isFailed = status === ImportStatus.failed;
  const isLoading = status === ImportStatus.loading;
  const hasError =
    status === ImportStatus.failed ||
    status === ImportStatus.completedWithErrors;

  return (
    <HistoryTableRow data-testid="PlatformAdminUI-HistoryLog-HistoryTableRow">
      <StyledTableCell>{fileName}</StyledTableCell>
      <StyledTableCell>{createdBy}</StyledTableCell>
      <StyledDateTableCell>
        {new Date(createdDate)
          .toLocaleString()
          .replace(/,/i, '')
          .replace(/:\d\d\s/, ' ')}
      </StyledDateTableCell>
      <StyledTableCell sx={{ overflow: 'hidden' }}>
        {entityType}
      </StyledTableCell>
      <StyledTableCell>{numOfRecords}</StyledTableCell>
      <StyledTableCell>{fileDisplay}</StyledTableCell>
      <StyledStatusCell>
        <StyledBox>
          {isCompleted && <CompletedIcon icon={Icons.checkCircle} />}
          {isCompletedWithErrors && (
            <CompletedWithErrorsIcon viewBox="0 2 16 9" />
          )}
          {isFailed && <FailedIcon viewBox="1 4 16 10" />}
          {isLoading ? (
            <StyledBox>
              <ProgressBar variant="determinate" value={progress || 0} />
              <StyledProgressBarText variant="caption">{`${
                progress || 0
              }% complete`}</StyledProgressBarText>
            </StyledBox>
          ) : (
            status
          )}
        </StyledBox>
      </StyledStatusCell>
      <TableCell
        sx={{
          minWidth: '58px',
          textAlign: 'center',
        }}
      >
        <IconButton
          onClick={onClick}
          size="large"
          aria-label="menu icon button"
          data-testid="PlatformAdminUI-HistoryLog-MenuIconButton"
          sx={{
            padding: '0',
            width: '24px',
            height: '24px',
          }}
        >
          <MenuIcon
            icon={Icons.menu}
            style={{
              visibility: hasError ? 'visible' : 'hidden',
            }}
          />
        </IconButton>
      </TableCell>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        transformOrigin={{
          vertical: -32,
          horizontal: 'right',
        }}
        onClose={() => setAnchorEl(null)}
      >
        {hasError && <DownloadErrorButton jobId={id} />}
      </Menu>
      <FileModal
        file={{
          name: fileName,
          size: 1000,
          lastModified: lastModifiedDate,
        }}
        open={openModal}
        closeModal={() => setOpenModal(false)}
      />
    </HistoryTableRow>
  );
};

const HistoryLog = () => {
  const [order, setOrder] = useState<Order>('desc');
  const [orderField, setOrderField] = useState('createdDate');
  const [searchString, setSearchString] = useState('');
  const { goToImportPage } = useContext(DataImportContext);

  const [postsPerPage, setPostsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading } = useGetDataImportHistory();

  const historyLog: HistoryTableData[] = (data?.data ?? []).map((job) =>
    formatJobHistory(job),
  );

  const lastPostIndex = currentPage * postsPerPage;
  const firstPostIndex = lastPostIndex - postsPerPage;
  let currentPosts: HistoryTableData[] = historyLog.slice(
    firstPostIndex,
    lastPostIndex,
  );
  let postsFused: HistoryTableData[] = [];

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    event.preventDefault();
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: number) => {
    setPostsPerPage(event);
    setCurrentPage(1);
  };

  const sortHandler = (id: string) => {
    const isAsc = orderField === id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderField(id);
  };

  const downloadHistory = () => {
    const csvDownloadLink = document.createElement('a');
    const csvHeader = [
      'Description',
      'Created by',
      'Date',
      'File type',
      '# of records',
      'File size',
      'Processing status',
    ].join(',');
    const csvData: BlobPart = historyLog
      .map(
        ({
          fileName,
          createdBy,
          createdDate,
          entityType,
          numOfRecords,
          fileSize,
          status,
        }) =>
          [
            fileName,
            createdBy,
            createdDate,
            entityType,
            numOfRecords,
            fileSize,
            status,
          ].join(','),
      )
      .join('\n');
    const file = new Blob([`${csvHeader}\n${csvData}`], { type: 'text/plain' });
    csvDownloadLink.href = URL.createObjectURL(file);
    csvDownloadLink.download = `historyLog.csv`;
    csvDownloadLink.click();
  };

  const paginationRef = useRef(null);
  const firstPageButton =
    paginationRef.current?.children[1]?.children[0]?.children[1]?.children[0];

  useEffect(() => {
    const { pendo, location } = window;
    pendo?.location?.setUrl(`${location.href}/history`);
    return () => pendo?.location?.useBrowserUrl();
  }, []);

  useEffect(() => {
    if (
      searchString &&
      firstPageButton &&
      currentPage > Math.round(postsFused.length / postsPerPage)
    ) {
      firstPageButton.click();
    }
  }, [
    searchString,
    postsFused.length,
    postsPerPage,
    currentPage,
    firstPageButton,
  ]);

  if (searchString) {
    const options = {
      shouldSort: true,
      threshold: 0.25,
      keys: [
        'fileName',
        'createdBy',
        'createdDate',
        'lastModifiedBy',
        'lastModifyDate',
        'fileType',
        'numberOfRecords',
        'fileSize',
        'processingState',
      ],
    };

    const fuse = new Fuse(historyLog, options);
    postsFused = fuse.search(searchString);
    currentPosts = postsFused.slice(firstPostIndex, lastPostIndex);
  } else {
    currentPosts = historyLog.slice(firstPostIndex, lastPostIndex);
  }

  return (
    <>
      <Grid container justifyContent="flex-end" sx={{ position: 'relative' }}>
        <Tooltip title="Download CSV file">
          <DownloadIcon
            data-testid="PlatformAdminUI-HistoryLog-DownloadIcon"
            icon={Icons.download}
            size={24}
            onClick={downloadHistory}
            role="button"
          />
        </Tooltip>
        <HistorySearch
          freeSolo
          hiddenLabel
          id="Search-label"
          options={[]}
          disableClearable
          placeholder="Search"
          onInputChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchString(e.target?.value?.trim())
          }
          data-testid="PlatformAdminUI-HistoryLog-SearchField"
        />
        <ImportButton
          id="searchbarButton"
          color="primary"
          variant="contained"
          onClick={goToImportPage}
          data-testid="PlatformAdminUI-HistoryLog-ImportButton"
        >
          Bulk import
        </ImportButton>
      </Grid>
      <>
        {isLoading ? (
          <SkeletonAllRows />
        ) : (
          <>
            <TableContainer sx={{ height: '60vh' }}>
              <Table>
                <TableHead className="m-table-header">
                  {headCells.map((cell) => (
                    <TableCell
                      key={cell.id}
                      sortDirection={orderField === cell.id ? order : false}
                      align="left"
                    >
                      <TableSortLabel
                        active={orderField === cell.id}
                        direction={orderField === cell.id ? order : 'asc'}
                        onClick={() => sortHandler(cell.id)}
                        data-testid="PlatformAdminUI-HistoryLog-TableSortLabel"
                        hideSortIcon
                      >
                        <Typography
                          sx={{ fontWeight: 800, textWrap: 'nowrap' }}
                          variant="body1"
                        >
                          {cell.label}
                        </Typography>
                        <span
                          className={clsx(
                            'm-table-header__header__col__text',
                            'sort_icon',
                            cell.id === orderField && `${order}_icon`,
                          )}
                        />
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableHead>
                <TableBody>
                  {sortData(currentPosts, order, orderField).map((entry) => (
                    <HistoryRow key={entry.createdDate} entry={entry} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid
              height="150px"
              marginTop="30px"
              container
              justifyContent="center"
              sx={{
                '& #nui-rows-per-page': {
                  display: 'none',
                },
              }}
            >
              <Pagination
                count={
                  searchString
                    ? Math.round(postsFused.length / postsPerPage)
                    : Math.round(historyLog.length / postsPerPage)
                }
                totalItem={searchString ? postsFused.length : historyLog.length}
                perPageOptions={[10, 25, 50, 100]}
                data-testid="PlatformAdminUI-HistoryLog-Pagination"
                onChange={handleChangePage}
                onRowChange={handleChangeRowsPerPage}
                ref={paginationRef}
              />
            </Grid>
          </>
        )}
      </>
    </>
  );
};

export default HistoryLog;
